import { useEffect } from "react";
import { authService } from "services/authService";

const APPLICATION_TOKEN =
  process.env.REACT_APP_ADMIN_WIDGET_APPLICATION_TOKEN ||
  "0cf8bcac-61db-40a1-99a9-af2c1828b440";
const ADMIN_SERVER_ID =
  process.env.REACT_APP_ADMIN_WIDGET_SERVER_ID ||
  "78179520-866f-4fb4-9123-22889404c531";

declare global {
  interface Window {
    initStrikeUpChatWidget: (config: Record<string, unknown>) => void;
    StrikeUpChat?: {
      open: (options?: {
        message?: string;
        openContext?: string;
        /** @deprecated Renamed to openContext. */
        developerContext?: string;
      }) => void;
      close: () => void;
      destroy: () => void;
      getDisplayType: () => string | null;
      isReady: () => boolean;
      onReady: (cb: (displayType: string | null) => void) => () => void;
      setServerAuthorizations?: (
        authorizations: ServerAuthorization[],
      ) => boolean;
    };
  }
}

interface ServerAuthorization {
  serverId: string;
  authData: Record<string, string>;
}

// Refresh our own token before handing it to the widget when it has less than this left, so a
// tool call made right after never runs on a token that expires mid-request.
const TOKEN_MIN_REMAINING_MS = 2 * 60 * 1000;
// How often to look for a token refreshed by another code path (axios interceptor, timer, tab).
const TOKEN_WATCH_INTERVAL_MS = 30 * 1000;

/** The admin toolkit authenticates as the signed-in user, with the app's own access token. */
function buildServerAuthorizations(
  token: string | null,
): ServerAuthorization[] {
  return token
    ? [
        {
          serverId: ADMIN_SERVER_ID,
          authData: { Authorization: `Bearer ${token}` },
        },
      ]
    : [];
}

/** Expiry (ms epoch) of the stored access token: the saved tokenExpiry, else the JWT's exp claim. */
function getAccessTokenExpiry(): number | null {
  const stored = parseInt(localStorage.getItem("tokenExpiry") ?? "", 10);
  if (!Number.isNaN(stored)) return stored;
  const token = authService.getAccessToken();
  try {
    const payload = token?.split(".")[1];
    if (!payload) return null;
    const exp = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** The current access token, refreshed first if it is expired or about to expire. */
async function getFreshAccessToken(): Promise<string | null> {
  const expiry = getAccessTokenExpiry();
  if (
    authService.getRefreshToken() &&
    expiry !== null &&
    expiry - Date.now() < TOKEN_MIN_REMAINING_MS
  ) {
    await authService.refreshToken();
  }
  return authService.getAccessToken();
}

export function useWidgetInit() {
  useEffect(() => {
    if (typeof window.initStrikeUpChatWidget !== "function") return;

    const token = localStorage.getItem("accessToken");
    const userRaw = localStorage.getItem("user");
    const user = userRaw ? JSON.parse(userRaw) : null;
    const serverAuthorizations = buildServerAuthorizations(token);
    const themeMode = localStorage.getItem("theme-mode");
    const systemPrefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const isDark =
      themeMode === "dark" || (themeMode !== "light" && systemPrefersDark);

    window.initStrikeUpChatWidget({
      applicationToken: APPLICATION_TOKEN,
      serverAuthorizations,
      // Asked before every message / tool approval: the widget would otherwise keep using the
      // token it was mounted with long after the app refreshed its own (401s on every tool).
      getServerAuthorizations: async () =>
        buildServerAuthorizations(await getFreshAccessToken()),
      theme: isDark ? "dark" : "light",
      ...(user?.id && { userId: user.id }),
      ...(user?.email && {
        userDisplayName: user.email,
        userEmail: user.email,
      }),
      ...(user?.companyId && { userCompanyId: user.companyId }),
      ...(user?.companyName && { userCompanyName: user.companyName }),
      ...(user?.id && { handoffUserId: user.id }),
    });

    // Push every token change to the widget as well, so the stored credential stays valid for
    // work that is not preceded by a message (scheduled workflows, resumed jobs).
    let lastPushedToken = token;
    const pushIfChanged = () => {
      const current = authService.getAccessToken();
      if (!current || current === lastPushedToken) return;
      if (
        window.StrikeUpChat?.setServerAuthorizations?.(
          buildServerAuthorizations(current),
        )
      ) {
        lastPushedToken = current;
      }
    };
    const interval = setInterval(pushIfChanged, TOKEN_WATCH_INTERVAL_MS);
    window.addEventListener("storage", pushIfChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", pushIfChanged);
    };
  }, []);
}
